import { useMemo } from 'react'
import { resolvePrismicLink } from '../utils/prismicLinkResolver'
import usePageContext from './usePageContext'
import { SEOProps } from '../components/Seo'
import { ContactFormProps } from '../components/organisms/ContactForm/types'
import useContactForm from './useContactForm'
import { mapButtonProps } from '../components/atoms/Button/utils'
import { ButtonProps } from 'theme-ui'
import { useLocation } from '@reach/router'
import useSiteData from './useSiteData'

export interface PageSlices {
  numbered_list?: GatsbyTypes.PrismicHomepageBodyServingYouTechFragment
}

export interface UsePageDataReturnType {
  hero?: {
    withSmallGradientBg: boolean
    withGradientBg: boolean
    icon?: {
      alt?: string
      url?: string
      dimensions?: {
        height?: number
        width?: number
      }
    }
    title?: string
    subtitle?: string
    desc?: string
    caption?: string
    cta?: ButtonProps
    media?: {
      image?: {
        publicURL?: string
        alt?: string
      }
      video?: {
        url?: string
      }
    }
    form?: ContactFormProps
  }
  seo: SEOProps
  slices: Array<
    | GatsbyTypes.PrismicPageBodyNumberedListFragment
    | GatsbyTypes.PrismicPageBodyTabsFragment
    | GatsbyTypes.PrismicPageBodyLogosBannerFragment
    | GatsbyTypes.PrismicPageBodyCallToActionFragment
    | GatsbyTypes.PrismicPageBodyReasonsWhyFragment
    | GatsbyTypes.PrismicPageBodyHeroBannerFragment
    | GatsbyTypes.PrismicPageBodyDownloadPlatformsFragment
  >
}

const usePageData = (data?: GatsbyTypes.PrismicPageQuery): UsePageDataReturnType => {
  const { group, lang } = usePageContext()
  const { pathname } = useLocation()
  const { siteUrl, logoAbsoluteUrl } = useSiteData()

  const { hero_contact_form } = data?.prismicPage?.data || {}

  const form = useContactForm(hero_contact_form?.document)

  const pageUrl = `${siteUrl}${pathname}`

  return useMemo(() => {
    const {
      is_hero_enabled,
      is_hero_gradient,
      title,
      subtitle,
      description,
      icon,
      image,
      video,
      button_text,
      button_type,
      button_link,
      button_is_centered,
      button_caption,
      button_caption_mobile_size,
      button_caption_desktop_size,
      hero_contact_form,
      body,
      seo_title = '',
      seo_description,
      seo_image,
      seo_twitter_card,
      seo_twitter_title,
      seo_twitter_description,
      seo_twitter_site,
      seo_twitter_image,
      seo_twitter_creator,
      seo_sm_context,
      seo_sm_type,
      seo_sm_name,
      seo_sm_alternate_name,
      seo_sm_description,
      seo_sm_url,
      seo_sm_logo,
      seo_sm_same_as = [],
      seo_sm_contact_points = []
    } = data?.prismicPage?.data || {}

    return {
      hero: is_hero_enabled
        ? {
            withSmallGradientBg: is_hero_gradient === true,
            withGradientBg: is_hero_gradient === true,
            icon: icon,
            title: title?.text,
            isMainHeading: true,
            subtitle: subtitle?.text,
            desc: '',
            caption: description?.html,
            cta: mapButtonProps(
              button_type,
              button_text,
              resolvePrismicLink(button_link, group),
              button_link?.link_type,
              button_caption,
              button_caption_mobile_size,
              button_caption_desktop_size,
              button_is_centered
            ),
            media: {
              image: video?.url
                ? undefined
                : {
                    publicURL: image?.url,
                    alt: image?.alt,
                    childImageSharp: image && {
                      gatsbyImageData: image.gatsbyImageData
                    },
                    width: image?.dimensions?.width,
                    height: image?.dimensions?.height
                  },
              video: {
                url: video?.url
              }
            },
            form: hero_contact_form?.document ? form : undefined
          }
        : undefined,
      slices: (body || []).filter((slice): slice is UsePageDataReturnType['slices'][number] => !!slice),
      seo: {
        title: seo_title,
        description: seo_description,
        ogDescription: seo_description,
        image: seo_image?.url,
        lang,
        url: pageUrl,
        meta: [
          seo_twitter_card
            ? {
                property: `twitter:card`,
                content: seo_twitter_card
              }
            : undefined,
          seo_twitter_title
            ? {
                property: `twitter:title`,
                content: seo_twitter_title
              }
            : undefined,
          seo_twitter_description
            ? {
                property: `twitter:description`,
                content: seo_twitter_description
              }
            : undefined,
          {
            property: `twitter:site`,
            content: seo_twitter_site
          },
          seo_twitter_image?.url
            ? {
                property: `twitter:image`,
                content: seo_twitter_image?.url
              }
            : undefined,
          seo_twitter_creator
            ? {
                property: `twitter:creator`,
                content: seo_twitter_creator
              }
            : undefined
        ],
        schemaMarkup: {
          '@context': seo_sm_context || 'https://schema.org',
          '@type': seo_sm_type || 'Organization',
          name: seo_sm_name,
          description: seo_sm_description,
          url: seo_sm_url || pageUrl,
          logo: seo_sm_logo?.url || logoAbsoluteUrl,
          alternateName: seo_sm_alternate_name,
          sameAs: (seo_sm_same_as || []).map((item) => item?.seo_sm_same_as_item),
          contactPoint: (seo_sm_contact_points || []).map((point) => ({
            '@type': point?.seo_sm_type,
            contactType: point?.seo_sm_contact_type,
            name: point?.seo_sm_name,
            description: point?.seo_sm_description,
            telephone: point?.seo_sm_telephone,
            email: point?.seo_sm_email,
            hoursAvailable: point?.seo_sm_hours_available
          }))
        }
      }
    }
  }, [data, form, group, lang, pageUrl, logoAbsoluteUrl])
}

export default usePageData
